import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Package,
  Plus,
  Search,
  Filter,
  Truck,
  CheckCircle,
  Clock,
  ShoppingCart,
  ExternalLink,
  Trash2,
  Edit,
  Calendar,
  Hash,
  CheckSquare,
  Square,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PART_ORDER_STATUSES = {
  to_order: { label: "À commander", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: ShoppingCart },
  ordered: { label: "Commandé", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Truck },
  received: { label: "Reçu", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: CheckCircle },
};

const QUOTE_STATUS_BADGES = {
  accepted: { label: "Devis accepté", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  pending: { label: "Devis en attente", color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
};

function StatusBadge({ status }) {
  const config = PART_ORDER_STATUSES[status] || PART_ORDER_STATUSES.to_order;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

export default function PartsToOrderPage() {
  const [parts, setParts] = useState([]);
  const [stats, setStats] = useState({ to_order: 0, ordered: 0, received: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  
  const [repairs, setRepairs] = useState([]);
  const [formData, setFormData] = useState({
    repair_id: "",
    part_name: "",
    part_reference: "",
    quantity: 1,
    supplier: "",
    notes: "",
  });
  
  const [editFormData, setEditFormData] = useState({
    status: "",
    order_date: "",
    expected_date: "",
    received_date: "",
    order_reference: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [partsRes, statsRes, repairsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/parts-to-order`, { headers }),
        axios.get(`${API_URL}/api/admin/parts-to-order/stats`, { headers }),
        axios.get(`${API_URL}/api/repairs`, { headers }),
      ]);
      
      setParts(partsRes.data);
      setStats(statsRes.data);
      setRepairs(repairsRes.data.filter(r => r.status === "pending_parts" || r.status === "diagnostic" || r.status === "quote_accepted"));
      setSelectedIds([]); // Reset selection on data refresh
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered parts based on search and status
  const filteredParts = parts.filter((p) => {
    const matchesSearch =
      p.part_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.ticket_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.supplier && p.supplier.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredParts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredParts.map(p => p.id));
    }
  };

  const toggleSelectOne = (partId) => {
    if (selectedIds.includes(partId)) {
      setSelectedIds(selectedIds.filter(id => id !== partId));
    } else {
      setSelectedIds([...selectedIds, partId]);
    }
  };

  const isAllSelected = filteredParts.length > 0 && selectedIds.length === filteredParts.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < filteredParts.length;

  // Bulk actions
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedIds.length === 0) {
      toast.error("Sélectionnez au moins une pièce");
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/admin/parts-to-order/bulk-update`,
        {
          part_order_ids: selectedIds,
          status: newStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedIds.length} pièce(s) mise(s) à jour: ${PART_ORDER_STATUSES[newStatus].label}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleAddPart = async (e) => {
    e.preventDefault();
    if (!formData.repair_id || !formData.part_name) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/admin/parts-to-order`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pièce ajoutée à la liste");
      setAddDialogOpen(false);
      setFormData({ repair_id: "", part_name: "", part_reference: "", quantity: 1, supplier: "", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleUpdatePart = async (e) => {
    e.preventDefault();
    if (!selectedPart) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/api/admin/parts-to-order/${selectedPart.id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Commande mise à jour");
      setEditDialogOpen(false);
      setSelectedPart(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDeletePart = async (partId) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/admin/parts-to-order/${partId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Commande supprimée");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleQuickStatusChange = async (partId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/api/admin/parts-to-order/${partId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Statut mis à jour: ${PART_ORDER_STATUSES[newStatus].label}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const openEditDialog = (part) => {
    setSelectedPart(part);
    setEditFormData({
      status: part.status,
      order_date: part.order_date || "",
      expected_date: part.expected_date || "",
      received_date: part.received_date || "",
      order_reference: part.order_reference || "",
      notes: part.notes || "",
    });
    setEditDialogOpen(true);
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="parts-to-order-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-chivo text-3xl font-bold text-foreground flex items-center gap-3">
              <Package className="w-8 h-8 text-primary" />
              Pièces à commander
            </h1>
            <p className="text-muted-foreground mt-1">Gérez les commandes de pièces pour vos réparations</p>
          </div>
          <Button 
            onClick={() => setAddDialogOpen(true)} 
            className="bg-primary hover:bg-primary/90"
            data-testid="add-part-order-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une pièce
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <ShoppingCart className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.to_order}</p>
                  <p className="text-xs text-muted-foreground">À commander</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Truck className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.ordered}</p>
                  <p className="text-xs text-muted-foreground">En transit</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.received}</p>
                  <p className="text-xs text-muted-foreground">Reçues</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20">
                  <Package className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par pièce, ticket, client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-secondary/50"
              data-testid="part-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-secondary/50" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="to_order">À commander</SelectItem>
              <SelectItem value="ordered">Commandé</SelectItem>
              <SelectItem value="received">Reçu</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.length > 0 && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium">
                  <CheckSquare className="w-4 h-4 inline mr-1" />
                  {selectedIds.length} sélectionné(s)
                </span>
                <div className="h-4 border-l border-border" />
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                  onClick={() => handleBulkStatusChange("ordered")}
                  disabled={bulkActionLoading}
                >
                  <Truck className="w-4 h-4 mr-1" />
                  Marquer commandé
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                  onClick={() => handleBulkStatusChange("received")}
                  disabled={bulkActionLoading}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Marquer reçu
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedIds([])}
                >
                  Désélectionner
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Parts List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : filteredParts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune pièce à commander</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Select All Header */}
            <div className="flex items-center gap-3 px-4 py-2 bg-secondary/30 rounded-lg">
              <Checkbox 
                checked={isAllSelected}
                onCheckedChange={toggleSelectAll}
                className="border-muted-foreground"
                data-testid="select-all-checkbox"
              />
              <span className="text-sm text-muted-foreground">
                {isAllSelected ? "Tout désélectionner" : "Tout sélectionner"}
              </span>
            </div>
            
            {filteredParts.map((part) => (
              <Card 
                key={part.id} 
                className={`bg-card border-border hover:border-primary/30 transition-colors ${selectedIds.includes(part.id) ? 'ring-1 ring-primary/50' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <Checkbox 
                      checked={selectedIds.includes(part.id)}
                      onCheckedChange={() => toggleSelectOne(part.id)}
                      className="mt-1 border-muted-foreground"
                      data-testid={`select-part-${part.id}`}
                    />
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="font-medium text-lg">{part.part_name}</h3>
                            <StatusBadge status={part.status} />
                            {/* Quote status badge */}
                            {part.quote_status ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${QUOTE_STATUS_BADGES[part.quote_status]?.color || QUOTE_STATUS_BADGES.pending.color}`}>
                                {QUOTE_STATUS_BADGES[part.quote_status]?.label || "Devis en attente"}
                              </span>
                            ) : (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${QUOTE_STATUS_BADGES.pending.color}`}>
                                ⏳ Devis en attente
                              </span>
                            )}
                            {part.quantity > 1 && (
                              <span className="text-xs bg-secondary px-2 py-0.5 rounded">×{part.quantity}</span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span className="font-mono text-primary">{part.ticket_number}</span>
                            <span>•</span>
                            <span>{part.client_name}</span>
                            <span>•</span>
                            <span>{part.device_info}</span>
                          </div>
                          {(part.part_reference || part.supplier) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              {part.part_reference && (
                                <span className="flex items-center gap-1">
                                  <Hash className="w-3 h-3" />
                                  Réf: {part.part_reference}
                                </span>
                              )}
                              {part.supplier && (
                                <span className="flex items-center gap-1">
                                  <Truck className="w-3 h-3" />
                                  {part.supplier}
                                </span>
                              )}
                              {part.order_reference && (
                                <span className="flex items-center gap-1">
                                  <Package className="w-3 h-3" />
                                  Cmd: {part.order_reference}
                                </span>
                              )}
                            </div>
                          )}
                          {(part.order_date || part.expected_date) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                              {part.order_date && (
                                <span className="flex items-center gap-1 text-blue-400">
                                  <Calendar className="w-3 h-3" />
                                  Commandé: {part.order_date}
                                </span>
                              )}
                              {part.expected_date && (
                                <span className="flex items-center gap-1 text-amber-400">
                                  <Clock className="w-3 h-3" />
                                  Prévu: {part.expected_date}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Quick status buttons */}
                          {part.status === "to_order" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                              onClick={() => handleQuickStatusChange(part.id, "ordered")}
                            >
                              <Truck className="w-4 h-4 mr-1" />
                              Commandé
                            </Button>
                          )}
                          {part.status === "ordered" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                              onClick={() => handleQuickStatusChange(part.id, "received")}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Reçu
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(part)}
                            data-testid={`edit-part-${part.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive hover:bg-destructive/10"
                            onClick={() => handleDeletePart(part.id)}
                            data-testid={`delete-part-${part.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`/repairs/${part.repair_id}`, "_blank")}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Part Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo">Ajouter une pièce à commander</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddPart} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Réparation concernée *</Label>
                <Select
                  value={formData.repair_id}
                  onValueChange={(v) => setFormData({ ...formData, repair_id: v })}
                  required
                >
                  <SelectTrigger className="bg-secondary/50" data-testid="repair-select">
                    <SelectValue placeholder="Sélectionner une réparation" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {repairs.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        Aucune réparation en attente de pièces
                      </div>
                    ) : (
                      repairs.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.ticket_number} - {r.client_name} ({r.device_brand} {r.device_model})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Nom de la pièce *</Label>
                <Input
                  value={formData.part_name}
                  onChange={(e) => setFormData({ ...formData, part_name: e.target.value })}
                  placeholder="Ex: Écran iPhone 14 Pro"
                  required
                  className="bg-secondary/50"
                  data-testid="part-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Référence</Label>
                  <Input
                    value={formData.part_reference}
                    onChange={(e) => setFormData({ ...formData, part_reference: e.target.value })}
                    placeholder="SCR-IP14P"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quantité</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Fournisseur</Label>
                <Input
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  placeholder="Nom du fournisseur"
                  className="bg-secondary/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes supplémentaires..."
                  className="bg-secondary/50"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="confirm-add-part">
                  Ajouter
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Part Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo">
                Modifier la commande
                {selectedPart && (
                  <span className="block text-sm font-normal text-muted-foreground mt-1">
                    {selectedPart.part_name} - {selectedPart.ticket_number}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdatePart} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={editFormData.status}
                  onValueChange={(v) => setEditFormData({ ...editFormData, status: v })}
                >
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to_order">À commander</SelectItem>
                    <SelectItem value="ordered">Commandé</SelectItem>
                    <SelectItem value="received">Reçu</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de commande</Label>
                  <Input
                    type="date"
                    value={editFormData.order_date}
                    onChange={(e) => setEditFormData({ ...editFormData, order_date: e.target.value })}
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date prévue</Label>
                  <Input
                    type="date"
                    value={editFormData.expected_date}
                    onChange={(e) => setEditFormData({ ...editFormData, expected_date: e.target.value })}
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de réception</Label>
                  <Input
                    type="date"
                    value={editFormData.received_date}
                    onChange={(e) => setEditFormData({ ...editFormData, received_date: e.target.value })}
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>N° de commande</Label>
                  <Input
                    value={editFormData.order_reference}
                    onChange={(e) => setEditFormData({ ...editFormData, order_reference: e.target.value })}
                    placeholder="REF-123"
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  placeholder="Notes supplémentaires..."
                  className="bg-secondary/50"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="confirm-edit-part">
                  Enregistrer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
